import { useState } from 'react';
import { auth, db } from '@/lib/firebase';
import { collection, addDoc, serverTimestamp, deleteDoc, doc } from 'firebase/firestore';
import { sendInviteSignInLink } from '@/lib/inviteEmailLink';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import type { UserRole } from '@/types';

type InviteRole = Exclude<UserRole, 'owner' | 'pending'>;

const INVITE_ROLE_OPTIONS: { value: InviteRole; label: string }[] = [
  { value: 'admin', label: 'Administrador' },
  { value: 'recruiter', label: 'Reclutador' },
  { value: 'viewer', label: 'Solo lectura' },
];

interface AddTeamMemberModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  onSaved: () => void;
}

export function AddTeamMemberModal({
  open,
  onOpenChange,
  companyId,
  onSaved,
}: AddTeamMemberModalProps) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<InviteRole>('viewer');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) {
      setError('El correo es obligatorio.');
      return;
    }
    if (!companyId) {
      setError('No hay empresa asignada.');
      return;
    }
    setSending(true);
    let inviteId = '';
    try {
      const inviteRef = await addDoc(collection(db, 'userInvites'), {
        email: trimmedEmail,
        role,
        companyId,
        status: 'active',
        createdAt: serverTimestamp(),
      });
      inviteId = inviteRef.id;
      await sendInviteSignInLink(auth, trimmedEmail, inviteId);
      setSuccessMessage(
        `Se envió un correo a ${trimmedEmail} con un enlace para que establezca su contraseña y se una al equipo. El enlace caduca en unas horas.`
      );
      setEmail('');
      setRole('viewer');
      onSaved();
    } catch (err) {
      console.error('Error enviando invitación', err);
      if (inviteId) {
        await deleteDoc(doc(db, 'userInvites', inviteId)).catch((cleanupErr) => {
          console.warn('No se pudo limpiar la invitación fallida', cleanupErr);
        });
      }
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes('auth/unauthorized-continue-uri') || msg.includes('auth/unauthorized-continue-url')) {
        setError(
          'Firebase rechazó la URL de invitación. Agrega el dominio actual en Firebase Authentication > Settings > Authorized domains, o configura VITE_INVITE_CONTINUE_URL con un dominio autorizado.'
        );
        return;
      }
      if (msg.includes('permission') || msg.includes('Permission') || msg.includes('insufficient')) {
        setError(
          'No tienes permiso para invitar usuarios. Comprueba que eres administrador de la empresa.'
        );
      } else {
        setError(msg);
      }
    } finally {
      setSending(false);
    }
  };

  const handleClose = (open: boolean) => {
    if (!open) {
      setSuccessMessage('');
      setError('');
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invitar usuario al equipo</DialogTitle>
          <DialogDescription>
            Se enviará un correo con un enlace para que la persona establezca su contraseña y acceda al panel de la empresa.
          </DialogDescription>
        </DialogHeader>
        {successMessage ? (
          <div className="rounded-md border border-green-200 bg-green-50 p-4 text-sm text-green-800">
            {successMessage}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 py-4">
            {error && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="add-team-email">Correo</Label>
              <Input
                id="add-team-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="usuario@empresa.com"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-team-role">Rol</Label>
              <select
                id="add-team-role"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={role}
                onChange={(e) => setRole(e.target.value as InviteRole)}
              >
                {INVITE_ROLE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleClose(false)} disabled={sending}>
                Cancelar
              </Button>
              <Button type="submit" disabled={sending}>
                {sending ? 'Enviando invitación...' : 'Enviar invitación'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
